/**
 * assets/member-price.js
 * 会员价折扣模块 — PDP 价格联动核心逻辑
 *
 * 依赖（由 Liquid 提前注入）：
 *   window.__MP__              — layout/theme.liquid 注入（customer 状态 + planConfig）
 *   window.__MP__.productData  — PDP section 注入（variants 折扣数据）
 *   window.__pdp__config__     — pdp-assets.liquid 注入（fmt 货币格式化，与 pdp.js 共享）
 *
 * 触发时机：
 *   1. DOMContentLoaded — 页面首次加载
 *   2. variant:changed  — 变体切换（新版 pdp.js 的 PdpProductForm 派发）
 *   3. LUTE:AUTH:LOGIN  — 用户在当前页面弹窗登录成功（过滤 type:keep）
 */

(function () {
  'use strict';

  // ── 常量 ────────────────────────────────────────────────────────────────

  const SEL = {
    module:              '[data-mp-module]',
    // 已登录会员态 — 汇总条
    summaryToggle:       '[data-mp-summary-toggle]',
    summaryDetail:       '[data-mp-summary-detail]',
    totalSaving:         '[data-mp-total-saving]',
    baseSavingValue:     '[data-mp-base-saving-value]',
    memberSavingValue:   '[data-mp-member-saving-value]',
    // 未登录 / 非会员态
    checkboxBase:        '[data-mp-checkbox="base"]',
    checkboxMember:      '[data-mp-checkbox="member"]',
    baseSavingDisplay:   '[data-mp-base-saving-display]',
    memberSavingDisplay: '[data-mp-member-saving-display]',
    joinNow:             '[data-mp-join-now]',
  };

  // ── 工具函数 ─────────────────────────────────────────────────────────────

  /**
   * 货币格式化。
   *
   * 是否带货币代码由 settings.currency_code_enabled 决定（window.__MP__.currencyCodeEnabled）：
   * 价格节点、saving badge、汇总条 → withCurrencyCode()（= settings.currency_code_enabled），
   *   与 theme.min.js 行为对齐（其 moneyFormat 也随该 setting 切换）
   *
   * withCurrency=true  → money_with_currency_format（带货币代码，如 $139.99 USD）
   * withCurrency=false → moneyFormat（不带货币代码，如 $139.99）
   */
  function formatMoney(cents, withCurrency) {
    if (!cents && cents !== 0) return '';
    const cfg = window.__pdp__config__;
    if (!cfg) return '';
    const MP = window.__MP__ || {};
    const format = (withCurrency && MP.moneyWithCurrencyFormat) ? MP.moneyWithCurrencyFormat : cfg.moneyFormat;
    return window.T4SThemeSP?.Currency?.formatMoney(cents, format) || '';
  }

  /**
   * 读取 __MP__.i18n 注入的翻译文案（Liquid | t 注入，已随当前语言切换），缺失时回退英文默认值。
   * 注：Liquid 的 {{ }} 输出默认做 HTML 转义（& → &amp;），而登录组件用文本节点渲染
   * customDescription 不解析 HTML，故返回前统一解码实体，避免页面显示 &amp;。
   */
  function decodeHtmlEntities(str) {
    if (!str) return str;
    const doc = new DOMParser().parseFromString(str, 'text/html');
    return doc.body.textContent || '';
  }

  function i18n(key, fallback) {
    const dict = (window.__MP__ || {}).i18n || {};
    return decodeHtmlEntities(dict[key] || fallback);
  }

  /**
   * 从 window.__MP__.planConfig 读取当前用户对应的会员折扣节省百分比。
   *
   * planConfig 结构（由 theme.liquid 从 Shop Metafield lute.member_price_plan_config 注入）：
   *   discountMode: 'UNIFORM' | 'BY_TIER'
   *   uniformRate:  节省百分比整数（UNIFORM 模式，如 1 = 节省 1%）
   *   tierConfigs:  [{ memberLevel, discountRate }]（BY_TIER 模式）
   *   discountRate: 节省百分比整数（如 15 = 节省 15%）
   *
   * 返回值：节省的百分比整数（如 1 表示节省 1%）；读取失败时返回默认值 1。
   */
  function getMemberDiscountPct() {
    const MP = window.__MP__ || {};
    const plan = MP.planConfig || {};
    const memberLevel = parseInt((MP.customer || {}).memberLevel, 10) || 0;
    const DEFAULT_PCT = 1;

    if (plan.discountMode === 'UNIFORM') {
      // Number(null) === 0，isFinite(0) === true，会错误返回 0；
      // 用 plan.uniformRate != null 先排除 null/undefined
      if (plan.uniformRate == null) return DEFAULT_PCT;
      const rate = Number(plan.uniformRate);
      return isFinite(rate) ? rate : DEFAULT_PCT;
    }

    if (plan.discountMode === 'BY_TIER') {
      // 已登录会员：按 memberLevel 匹配对应 tier
      // 未登录 / 非会员（memberLevel = 0）：取 tierConfigs 中 discountRate 最小值作为展示折扣
      const tiers = plan.tierConfigs || [];
      if (memberLevel >= 1) {
        const tier = tiers.find(t => t.memberLevel === memberLevel);
        if (tier) {
          const rate = Number(tier.discountRate);
          return isFinite(rate) ? rate : DEFAULT_PCT;
        }
      } else {
        // 非登录态：取所有 tier 中最小的 discountRate 作为未登录展示值
        const validRates = tiers
          .map(t => Number(t.discountRate))
          .filter(r => isFinite(r) && r > 0);
        if (validRates.length > 0) return Math.min(...validRates);
      }
    }

    return DEFAULT_PCT;
  }
  function isVipMember() {
    const c = (window.__MP__ || {}).customer || {};
    if (!c.isLoggedIn) return false;
    return (parseInt(c.memberLevel, 10) || 0) >= 1;
  }

  function isLoggedIn() {
    return !!((window.__MP__ || {}).customer || {}).isLoggedIn;
  }

  /**
   * 返回展示货币相对于基准货币（shop.currency）的汇率。
   * FIXED_DISCOUNT 的 basePromotionValue 以基准货币计价，前端展示需乘此汇率。
   * 与 pdp.js / pdp-coupon-code-v2.liquid 的 crossCurrency + Shopify.currency.rate 处理对齐。
   */
  function getCurrencyRate() {
    if (!(window.__MP__ || {}).crossCurrency) return 1;
    return parseFloat((window.Shopify || {}).currency?.rate) || 1;
  }

  /**
   * 按 variantId 取该 variant 的折扣数据（含 product 级回退）
   * 返回结构：{ price, compareAtPrice, isMemberPrice, basePromotionType, basePromotionValue, memberPriceDisplay }
   */
  function getVariantData(variantId) {
    const pd   = (window.__MP__ || {}).productData || {};
    const vMap = pd.variants || {};
    const vd   = vMap[String(variantId)] || {};
    const mpc  = vd.mpc != null ? vd.mpc : {};
    return {
      price:              vd.price             || 0,
      compareAtPrice:     vd.compareAtPrice    || vd.price || 0,
      isMemberPrice:      mpc.isMemberPrice    === true,
      basePromotionType:  mpc.basePromotionType  || null,
      basePromotionValue: Number(mpc.basePromotionValue) || 0,
      memberPriceDisplay: mpc.memberPriceDisplay || 'VALUE',
    };
  }

  /**
   * 获取当前页面的 variantId
   * 优先读 URL ?variant=，回退取 productData 第一个 variant
   */
  function getCurrentVariantId() {
    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl) return fromUrl;
    const pd = (window.__MP__ || {}).productData || {};
    return Object.keys(pd.variants || {})[0] || null;
  }

  // ── 辅助：切换 member-price UI 和 coupon UI 的显隐 ──────────────────────
  // 两套 UI 显隐的唯一控制入口，避免 member-price.js 与 pdp.js 竞争覆盖。
  // coupon 的折扣内容由 pdp.js 负责，显隐由此处统一决策。
  function _setCouponVisible(visible) {
    // v2（data-mp-ui="coupon"）优先；旧版 pdp-coupon-code.liquid 回退到 <pdp-coupon>
    var couponWrap = document.querySelector('[data-mp-ui="coupon"]') || document.querySelector('pdp-coupon');
    if (!couponWrap) return;
    couponWrap.hidden = !visible;
    couponWrap.classList.toggle('is-hidden', !visible);
  }

  function _switchDiscountUI(showMember) {
    var memberWrap = document.querySelector('[data-mp-ui="member"]');
    if (memberWrap) memberWrap.hidden = !showMember;
    // showMember=true 时主动隐藏 coupon
    // showMember=false 时 coupon 显隐由 mp:ui-switched 后读 pdp.js 缓存的期望值决定
    if (showMember) _setCouponVisible(false);
    document.dispatchEvent(new CustomEvent('mp:ui-switched', {
      detail: { showMember: showMember, variantId: _currentVariantId }
    }));
  }

  // pdp:coupon-visibility：pdp.js 通知期望的折扣码可见性，只缓存不立即应用。
  // 实际应用在 mp:ui-switched 后执行，确保 member hidden 状态已就绪。
  var _pendingCouponVisible = null;
  document.addEventListener('pdp:coupon-visibility', function (e) {
    _pendingCouponVisible = e.detail.visible;
  });

  // mp:ui-switched：_switchDiscountUI 完成后派发，member hidden 状态已就绪。
  // showMember=false 时应用 pdp.js 缓存的期望可见性。
  document.addEventListener('mp:ui-switched', function (e) {
    if (e.detail && e.detail.showMember) return; // member 激活时 coupon 已在 _switchDiscountUI 里隐藏
    if (_pendingCouponVisible === null) return;   // pdp.js 未给出期望值（无折扣码 DOM）
    _setCouponVisible(_pendingCouponVisible);
    _pendingCouponVisible = null;
  });

  // ── 核心：初始化折扣模块 ─────────────────────────────────────────────────

  // 首屏 SSR 状态同步标记：
  // SSR 已按当前 variant 用 hidden 属性渲染出唯一激活的 UI（member-price-block / pdp-coupon），
  // 首屏初始化直接沿用该状态，避免 JS 重复切换造成"刷新时双 UI 闪现"。
  // 仅在用户真正切换 variant 后，才由 member-price.js 接管显隐切换。
  var _initVariantId   = null;
  var _hasUserSwitched = false;

  function initDiscountModule(variantId) {
    const moduleEl = document.querySelector(SEL.module);

    const vd       = getVariantData(variantId);
    const isMember = isVipMember();

    // variantId 不属于当前商品（vd.price=0），忽略本次调用
    if (!vd.price) return;

    if (_initVariantId === null) _initVariantId = variantId;

    // 判断当前 variant 是否应展示会员价 UI
    const mpcBaseValue = Number(vd.basePromotionValue) || 0;
    const couponWrap   = document.querySelector('[data-mp-ui="coupon"]');
    // pdp-coupon DOM 始终存在（anyVariantHasCoupon），需从 data-pdp-coupon-data 判断
    // 当前变体是否真的有折扣，而不是仅检查 DOM 是否存在
    const hasCouponUI  = (function () {
      if (!couponWrap) return false;
      var dataEl = couponWrap.querySelector('[data-pdp-coupon-data]');
      if (!dataEl) return false;
      try {
        var d = JSON.parse(dataEl.textContent);
        // basicDiscountCode：全局/国家折扣，所有变体共享
        if (d.basicDiscountCode && d.basicDiscountValue) return true;
        // variantDiscounts：当前变体有专属折扣
        if (Array.isArray(d.variantDiscounts) && d.variantDiscounts.some(function (vdItem) {
          return String(vdItem.variantId) === String(variantId);
        })) return true;
      } catch (e) {}
      return false;
    })();

    var showMemberUI;
    const isInitialRender = !_hasUserSwitched && String(variantId) === String(_initVariantId);
    if (isInitialRender) {
      // 首屏：沿用 SSR 决定的激活 UI，不再切换显隐
      const memberWrap = document.querySelector('[data-mp-ui="member"]');
      showMemberUI = !!(memberWrap && !memberWrap.hidden);
    } else {
      // 会员价优先，但 baseValue=0 且有折扣码时让位给折扣码
      showMemberUI = vd.isMemberPrice && (mpcBaseValue > 0 || !hasCouponUI);
      _hasUserSwitched = true;
      // 切换两套 UI 的显隐
      _switchDiscountUI(showMemberUI);
    }

    if (!showMemberUI || !moduleEl) {
      // 切到折扣码 UI 或无会员价模块：不需要初始化 member-price
      // 防御性派发 applyMember=false，清掉 PdpPrice 上一 variant 的会员价残留（§4.3.3）
      // 注意：isMemberVariant 必须为 false，否则 pdp-price._render() 会锁死在原价，
      //       导致折扣码勾选无法触发价格联动（mpcBaseValue=0 走折扣码 UI 的场景）
      if (moduleEl && !showMemberUI) {
        const detail = {
          variantId:       variantId,
          finalPrice:      vd.price,
          comparePrice:    vd.compareAtPrice > vd.price ? vd.compareAtPrice : null,
          basePrice:       vd.price,
          applyMember:     false,
          memberPrice:     vd.price,
          displayMode:     (vd.memberPriceDisplay || '').toUpperCase(),
          isMemberVariant: false, // 明确覆盖：此 variant 走折扣码路径，允许 _applyDiscount
        };
        // 直接 dispatch，跳过 _dispatchMemberPrice 里的自动 isMemberVariant 赋值
        document.dispatchEvent(new CustomEvent('pdp:member-price-change', {
          bubbles: true,
          detail: detail,
        }));
      }
      return;
    }

    moduleEl.hidden = false;

    // ── 计算各折扣金额 ──────────────────────────────────────────────────
    const price           = vd.price;
    const compareAtPrice  = vd.compareAtPrice;
    const displayMode     = vd.memberPriceDisplay.toUpperCase(); // 'VALUE' | 'PERCENT'

    // 基础折扣（可单独作用，节省金额按原价独立计算）
    // FIXED_DISCOUNT:   basePromotionValue = 基准货币立减金额（元），跨货币时需乘汇率
    // PERCENT_DISCOUNT: basePromotionValue = 节省百分比（如 10 = 节省 10%），无货币换算
    const currencyRate  = getCurrencyRate();
    let baseSavingCents = 0;
    let baseSavingPct   = 0; // 展示用百分比（PERCENT 模式）
    let basePctCalc     = 0; // 计算用整数百分比（展示与计算对齐）
    if (vd.basePromotionType === 'FIXED_DISCOUNT') {
      // basePromotionValue 以基准货币（shop.currency）计价，乘汇率换算为展示货币（cart.currency）
      baseSavingCents = Math.round(vd.basePromotionValue * currencyRate * 100); // 元 → 分
      baseSavingPct   = Math.round(price > 0 ? baseSavingCents / price * 100 : 0);
      basePctCalc     = baseSavingPct; // 展示与计算对齐，均用 round 后的整数百分比
    } else if (vd.basePromotionType === 'PERCENT_DISCOUNT') {
      baseSavingPct   = vd.basePromotionValue; // 直接就是节省百分比
      basePctCalc     = baseSavingPct;
      // 对齐 Function（§3.5.3）：实付 = ceil(原价×(1-pct/100))，节省 = 原价 - 实付
      // 用 price*(100-pct)/100 代替 price*(1-pct/100)，规避 JS 浮点精度问题
      baseSavingCents = Math.max(0, price - Math.ceil(price * (100 - baseSavingPct) / 100));
    }

    // 会员额外折扣：getMemberDiscountPct() 直接返回节省百分比
    // 口径对齐 Function（方案 §3.5.1）：totalPct = basePct + memberPct，
    //   实付 = ceil(原价 × (1 - totalPct/100))（§3.5.3 Shopify 向上取整到分）。
    // 基础折扣可单独作用，其节省 baseSavingCents 按原价单独计算；
    // 会员价节省 = 总节省（按 totalPct 从原价整体计算）- 基础折扣节省。
    const memberSavingPct   = getMemberDiscountPct();
    const totalPctCalc      = basePctCalc + memberSavingPct;
    const totalFinalCents   = Math.max(0, Math.ceil(price * (100 - totalPctCalc) / 100));
    const totalSavingCents  = Math.max(0, price - totalFinalCents);
    const memberSavingCents = Math.max(0, totalSavingCents - baseSavingCents);

    // 折后价 = Function 合并折扣后的实付
    const finalPrice       = totalFinalCents;

    // Total Save 展示百分比 = 基础 + 会员（PERCENT 模式文案）
    const totalSavingPct   = baseSavingPct + memberSavingPct;

    // ── 格式化展示文案 ──────────────────────────────────────────────────
    // cents: 节省金额（VALUE 模式用），pct: 节省百分比（PERCENT 模式用）
    function fmt(cents, pct) {
      return displayMode === 'PERCENT' ? `${pct}%` : formatMoney(cents, false);
    }

    // ── 分支：已登录会员 vs 未登录/非会员 ─────────────────────────────────
    // 切换 mp-summary 和 mp-options 的显隐
    const summaryEl = moduleEl.querySelector('[data-mp-summary]');
    const optionsEl = moduleEl.querySelector('[data-mp-options]');
    if (isMember) {
      if (summaryEl) summaryEl.hidden = false;
      if (optionsEl) optionsEl.hidden = true;
      _renderSummary(moduleEl, {
        totalText:  fmt(totalSavingCents, totalSavingPct),
        baseText:   fmt(baseSavingCents,  baseSavingPct),
        memberText: fmt(memberSavingCents, memberSavingPct),
        hasBase:    !!vd.basePromotionValue,
      });
      // 会员态无勾选框：直接派发最终会员价事件（划线价：compareAtPrice 有效时优先，否则有折扣时以基础价作划线）
      _dispatchMemberPrice({
        variantId:    variantId,
        finalPrice:   finalPrice,
        comparePrice: compareAtPrice > price ? compareAtPrice : (finalPrice < price ? price : null),
        basePrice:    price,
        applyMember:  finalPrice < price,
        memberPrice:  finalPrice,
        displayMode:  displayMode,
      });
    } else {
      if (summaryEl) summaryEl.hidden = true;
      if (optionsEl) optionsEl.hidden = false;
      _renderOptions(moduleEl, {
        price,
        baseSavingCents,
        baseSavingPct,
        memberSavingCents,
        memberSavingPct,
        fmt,
        hasBase: !!vd.basePromotionValue,
      });
    }
  }

  // ── 已登录会员态渲染 ─────────────────────────────────────────────────────

  function _renderSummary(moduleEl, { totalText, baseText, memberText, hasBase }) {
    // ① 汇总行：Total Save
    const totalEl = moduleEl.querySelector(SEL.totalSaving);
    if (totalEl) totalEl.textContent = totalText;

    // ② 明细行：base 明细行和加号随 hasBase 动态显隐（variant 切换时 baseValue 可能变化）
    const baseEl = moduleEl.querySelector(SEL.baseSavingValue);
    const baseItemEl = baseEl ? baseEl.closest('.mp-summary__item') : null;
    const plusEl = moduleEl.querySelector('.mp-summary__plus');
    if (baseItemEl) baseItemEl.hidden = !hasBase;
    if (plusEl)     plusEl.hidden     = !hasBase;
    if (baseEl && hasBase) baseEl.textContent = i18n('savePrefix', 'Save [text]').replace('[text]', baseText);

    const memberEl = moduleEl.querySelector(SEL.memberSavingValue);
    if (memberEl) memberEl.textContent = i18n('savePrefix', 'Save [text]').replace('[text]', memberText);

    // ③ Saving badge 交由 PdpPrice 渲染（§4.3.1 职责拆分）
    //    PdpPrice._render 依据 compareValue - finalPrice 统一计算并写入 [data-pdp-saving-badge]

    // ④ 展开/折叠：绑定点击事件（幂等），保持当前展开状态（变体切换时不收起）
    const toggleBtn = moduleEl.querySelector(SEL.summaryToggle);
    const detailEl  = moduleEl.querySelector(SEL.summaryDetail);
    if (toggleBtn && detailEl) {
      // 幂等绑定：只绑一次
      if (!toggleBtn.__mpBound) {
        toggleBtn.__mpBound = true;
        // 初始化时设为收起态
        toggleBtn.setAttribute('aria-expanded', 'false');
        detailEl.hidden = true;
        // 绑定切换事件
        toggleBtn.addEventListener('click', function () {
          const isExpanded = this.getAttribute('aria-expanded') === 'true';
          const next       = !isExpanded;
          this.setAttribute('aria-expanded', String(next));
          detailEl.hidden  = !next;
        });
      }
      // 变体切换时不改变展开状态，保持用户当前操作
    }
  }

  /**
   * 计算并派发会员价事件（§4.3.2 事件协议）。
   * PdpPrice 统一监听 pdp:member-price-change 完成价格 / compare / saving badge 渲染。
   * isMemberVariant：标记当前 variant 是否属于会员价 UI，供 PdpPrice 区分
   *   "会员价 UI variant 未勾选（显示基础价）" 与 "折扣码 UI variant（可叠加折扣码）"，
   *   避免上一 variant 的折扣码残留串入会员价 UI。
   */
  function _dispatchMemberPrice(detail) {
    const _pd  = (window.__MP__ || {}).productData || {};
    const _vd  = (_pd.variants || {})[String(detail.variantId)] || {};
    const _mpc = _vd.mpc || {};
    detail.isMemberVariant = _mpc.isMemberPrice === true;
    document.dispatchEvent(new CustomEvent('pdp:member-price-change', {
      bubbles: true,
      detail: detail,
    }));
  }

  // ── 未登录 / 非会员态渲染 ───────────────────────────────────────────────

  function _renderOptions(moduleEl, { price, baseSavingCents, baseSavingPct, memberSavingCents, memberSavingPct, fmt, hasBase }) {
    // 基础折扣行：根据 hasBase 控制显隐
    const baseOptionEl = moduleEl.querySelector('.mp-option--base');
    if (baseOptionEl) baseOptionEl.hidden = !hasBase;

    // 更新勾选框内的 Saving 文案
    const baseSavingDisplayEl = moduleEl.querySelector(SEL.baseSavingDisplay);
    if (baseSavingDisplayEl) baseSavingDisplayEl.textContent = fmt(baseSavingCents, baseSavingPct);

    const memberSavingDisplayEl = moduleEl.querySelector(SEL.memberSavingDisplay);
    if (memberSavingDisplayEl) memberSavingDisplayEl.textContent = fmt(memberSavingCents, memberSavingPct);

    const baseCb   = moduleEl.querySelector(SEL.checkboxBase);
    const memberCb = moduleEl.querySelector(SEL.checkboxMember);

    // ── 绑定 member checkbox 联动逻辑（幂等）──
    // 勾选 member：联动勾上 base
    // 取消 member：base 保持当前状态，用户可自行取消
    if (memberCb && !memberCb.__mpBound) {
      memberCb.__mpBound = true;
      memberCb.addEventListener('change', function () {
        if (this.checked && baseCb) {
          baseCb.checked = true;
          const baseCbParent = baseCb.closest('.mp-option');
          if (baseCbParent && !baseCbParent.hidden) {
            _mpTrack('mp_discount_click', { mp_option: 'base', mp_checked: true });
          }
        }
        _mpTrack('mp_discount_click', { mp_option: 'member', mp_checked: this.checked });
        _recalcPreviewPrice();
      });
    }

    // ── 绑定 base checkbox 联动逻辑（幂等）──
    if (baseCb && !baseCb.__mpBound) {
      baseCb.__mpBound = true;
      baseCb.addEventListener('change', function () {
        if (!this.checked && memberCb && memberCb.checked) {
          memberCb.checked = false;
          _mpTrack('mp_discount_click', { mp_option: 'member', mp_checked: false });
        }
        _mpTrack('mp_discount_click', { mp_option: 'base', mp_checked: this.checked });
        _recalcPreviewPrice();
      });
    }

    // ── Join now 按钮：SSR 已根据登录态加 hidden（member-price-block.liquid），
    // JS 只绑定点击事件，不再重复控制显隐。
    const joinNowEl = moduleEl.querySelector(SEL.joinNow);
    if (joinNowEl && !joinNowEl.__mpBound) {
      joinNowEl.__mpBound = true;
      joinNowEl.addEventListener('click', function (e) {
        e.preventDefault();
        if (isLoggedIn()) return;
        var loginDialog = document.querySelector('login-dialog');
        if (loginDialog && typeof loginDialog.open === 'function') {
          loginDialog.open(null, {
            activityType: 'member_price',
            customTitle: i18n('unlockTitle', 'Unlock Your Member Price'),
            customDescription: i18n('unlockDescription', 'Sign in to unlock member-only savings & exclusive offers.'),
            footerButton: {
              text: i18n('continueWithoutMembership', 'Continue without membership'),
              onClick: () => { loginDialog.close(); },
            },
          });
        }
      });
    }

    // variant 切换后同步重置 base disabled 状态（之前版本遗留，现已无 disabled 逻辑，保持 enabled）
    if (baseCb) baseCb.disabled = false;

    // 初始化一次预览价
    _recalcPreviewPrice();
  }

  /**
   * GTM dataLayer 埋点上报。
   * 在事件源头直接 push，绕开 label 双重 click / GTM 捕获时序问题。
   * @param {string} event  - dataLayer event 名称
   * @param {Object} params - 附加参数
   */
  function _mpTrack(event, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, params));
  }

  function _recalcPreviewPrice() {
    const moduleEl = document.querySelector(SEL.module);
    if (!moduleEl) return;

    const variantId = _currentVariantId;
    if (!variantId) return;
    const vd             = getVariantData(variantId);
    const price          = vd.price;
    const compareAtPrice = vd.compareAtPrice;
    // variantId 不属于当前商品时（price = 0），忽略本次计算
    if (!price) return;

    let baseSavingCents = 0;
    let basePctCalc     = 0;
    if (vd.basePromotionType === 'FIXED_DISCOUNT') {
      baseSavingCents = Math.round(vd.basePromotionValue * getCurrencyRate() * 100); // 元 → 分
      basePctCalc     = Math.round(price > 0 ? baseSavingCents / price * 100 : 0); // 与展示对齐
    } else if (vd.basePromotionType === 'PERCENT_DISCOUNT') {
      // 对齐 Function（§3.5.3）：实付 = ceil(原价×(1-pct/100))，节省 = 原价 - 实付
      // 用 price*(100-pct)/100 规避 JS 浮点精度问题
      baseSavingCents = Math.max(0, price - Math.ceil(price * (100 - vd.basePromotionValue) / 100));
      basePctCalc     = vd.basePromotionValue;
    }

    const baseCb        = moduleEl.querySelector(SEL.checkboxBase);
    const memberCb      = moduleEl.querySelector(SEL.checkboxMember);
    const baseChecked   = baseCb   ? baseCb.checked   : false;
    const memberChecked = memberCb ? memberCb.checked : false;
    const anyChecked    = baseChecked || memberChecked;

    // 口径对齐 Function（方案 §3.5.1）：基础折扣可单独作用（baseSavingCents 按原价算）；
    // 勾选 member = 应用 totalPct = basePct + memberPct 合并折扣，
    // 会员额外节省 = 总节省（Function 口径整体计算）- 基础折扣节省。
    const memberSavingPct   = getMemberDiscountPct();
    const baseActiveCents   = baseChecked ? baseSavingCents : 0;
    let previewPrice  = Math.max(0, price - baseActiveCents);
    let memberSavingCents = 0;
    if (memberChecked) {
      const totalPct    = basePctCalc + memberSavingPct;
      const totalFinal  = Math.max(0, Math.ceil(price * (100 - totalPct) / 100));
      previewPrice      = totalFinal;
      memberSavingCents = Math.max(0, (price - totalFinal) - baseActiveCents);
    }
    var hasDiscount = anyChecked && previewPrice < price;

    // ── 计算并派发会员价事件（§4.3.2 事件协议）──────────────────────────────
    // 划线价：compareAtPrice 有效（高于现价）时优先使用；否则会员折扣生效时以基础价作划线价
    const finalPrice    = hasDiscount ? previewPrice : price;
    const memberPrice   = Math.max(0, price - memberSavingCents);
    const comparePrice  = compareAtPrice > price ? compareAtPrice : (hasDiscount ? price : null);

    _dispatchMemberPrice({
      variantId: variantId,
      finalPrice: finalPrice,
      comparePrice: comparePrice,
      basePrice: price,
      applyMember: hasDiscount,
      memberPrice: memberPrice,
      displayMode: (vd.memberPriceDisplay || '').toUpperCase(),
    });
  }

  // ── 当前 variantId 状态（模块级变量，供 _recalcPreviewPrice 使用） ─────

  var _currentVariantId = null;

  // ── 事件监听 ─────────────────────────────────────────────────────────────

  // ── 事件：variant:changed（新版 PDP，原生事件）────────────────────────────
  document.addEventListener('variant:changed', function (e) {
    const variant = e.currentVariant || (e.detail && e.detail.variant) || e.detail;
    if (!variant || !variant.id) return;
    // 过滤非当前 PDP 商品的事件（如 quick view 弹窗触发）
    const _pd = (window.__MP__ || {}).productData || {};
    const _vMap = _pd.variants || {};
    if (Object.keys(_vMap).length > 0 && !_vMap[String(variant.id)]) return;
    _currentVariantId = variant.id;
    // Currency 未就绪时只更新 _currentVariantId，等 _init() 在 theme-ready 时统一初始化；
    // 已就绪时直接初始化（用户切换变体的常规路径）
    if (window.T4SThemeSP?.Currency?.formatMoney) {
      initDiscountModule(variant.id);
    }
    // 注意：不在此注册 theme-ready，避免与 _waitAndInit 竞争；
    // theme-ready 时 _init() 会读取最新的 _currentVariantId 执行初始化
  });

  // ── 事件：variant:changed（旧版 PDP，jQuery 事件）────────────────────────
  // jQuery trigger 先触发 jQuery 监听器，原生 addEventListener 后触发。
  // 若在此直接调 initDiscountModule，pdp-price._onVariantChanged（原生）还未执行，
  // _lastVariant 仍是旧 variant，pdp:member-price-change 会用错误的 variant 数据渲染。
  // 用 setTimeout(0) 推到下一宏任务，确保原生事件监听器先执行完。
  if (typeof jQuery !== 'undefined') {
    jQuery(document).on('variant:changed', function (e) {
      var variant = e.currentVariant;
      if (!variant || !variant.id) return;
      var _pd = (window.__MP__ || {}).productData || {};
      var _vMap = _pd.variants || {};
      if (Object.keys(_vMap).length > 0 && !_vMap[String(variant.id)]) return;
      _currentVariantId = variant.id;
      if (window.T4SThemeSP?.Currency?.formatMoney) {
        var _vid = variant.id;
        setTimeout(function () { initDiscountModule(_vid); }, 0);
      }
    });
  }

  // ── ATC / Buy Now 登录拦截 ───────────────────────────────────────────────
  // 未登录态勾选 member 折扣后点击「加购 / 立即购买」，先弹登录弹窗；
  // 登录成功（LUTE:AUTH:LOGIN）或关闭弹窗（LUTE:AUTH:CLOSED / CLOSE / 兜底）后重放原动作。
  //
  // kind 语义：
  //   'atc'      — 加购（打开 cart drawer）：新PDP data-pdp-atc、旧PDP data-atc-form、data-action-atc
  //   'checkout' — 直达结账：新PDP data-pdp-buy-now、data-buy-now、旧PDP quickly-buy-now-button、Shopify SA 按钮组
  //
  // 登录后跳转场景（location.href = shopifyLoginToken）：
  //   页面整体跳转再 redirect 回来，_mpAtcGate 丢失。
  //   跳转前把 { kind, selector } 存 sessionStorage，页面恢复后自动找按钮重触发。
  //
  // 触发方式：document 捕获阶段 click 统一拦截。
  //   - 旧 PDP ATC：interactable.min.js 在 `[data-atc-form]` 的 click 委托里
  //     preventDefault 后手动 fetch 加购，form submit 事件根本不派发 → 必须走 click。
  //   - 新 PDP ATC：pdp.js 绑定 form submit；click 的默认行为会派发 submit，
  //     click 捕获阶段拦截（preventDefault 阻止 submit 派发）同样有效。
  //   - 新 PDP Buy Now：pdp.js `_bindBuyNow` 的按钮 click。
  // submit 捕获仅作兜底（Enter 键提交、程序化 submit 等不经过按钮 click 的场景）。

  // 新 PDP 按钮选择器
  var SEL_NEW_PDP_ATC      = '[data-pdp-atc], [data-action-atc], [data-tsm-btn]';
  var SEL_NEW_PDP_CHECKOUT = '[data-pdp-buy-now], [data-buy-now]';
  // 旧 PDP 按钮选择器
  var SEL_OLD_PDP_ATC      = '[data-atc-form], [data-action-atc]';
  var SEL_OLD_PDP_CHECKOUT = 'quickly-buy-now-button';
  // 所有需拦截的按钮（合并，用于 closest 匹配）
  var SEL_ALL_INTERCEPT    = [SEL_NEW_PDP_ATC, SEL_NEW_PDP_CHECKOUT, SEL_OLD_PDP_ATC, SEL_OLD_PDP_CHECKOUT].join(', ');

  var _mpAtcGate = null; // { kind: 'atc'|'checkout', btn: Element, selector: string }

  var MP_GATE_KEY    = '__MP_GATE__';        // sessionStorage key
  var MP_SKIP_SA_KEY = '__MP_SKIP_SA__';     // localStorage key：SA 按钮组豁免到期时间戳
  var MP_SKIP_MA_KEY = '__MP_SKIP_MA__';     // localStorage key：移动端 [data-action-atc] 豁免到期时间戳
  var MP_SKIP_TTL    = 72 * 60 * 60 * 1000; // 72h（ms）

  /**
   * 检查某个豁免 key 是否仍在有效期内。
   */
  function _isSkipActive(storageKey) {
    try {
      var ts = parseInt(localStorage.getItem(storageKey), 10);
      return isFinite(ts) && Date.now() < ts;
    } catch (e) { return false; }
  }

  /**
   * 写入豁免时间戳（当前时间 + 72h）。
   */
  function _setSkip(storageKey) {
    try {
      localStorage.setItem(storageKey, String(Date.now() + MP_SKIP_TTL));
    } catch (e) {}
  }

  /**
   * 判断按钮是否属于"关闭弹窗后不再拦截"的豁免类型，并返回对应的豁免 key。
   * SA 按钮组 → MP_SKIP_SA_KEY
   * 移动端（触屏）sticky ATC [data-action-atc] → MP_SKIP_MA_KEY
   * 其他 → null（正常拦截）
   */
  function _getSkipKey(el) {
    if (!el) return null;
    var tag = (el.tagName || '').toUpperCase();
    // SA 按钮组
    if (tag === 'SHOPIFY-ACCELERATED-CHECKOUT' ||
        tag === 'SHOPIFY-ACCELERATED-CHECKOUT-CART' ||
        (el.classList && el.classList.contains('shopify-payment-button')) ||
        el.parentNode.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT'
      ) {
      return MP_SKIP_SA_KEY;
    }
    // 移动端（触屏）sticky ATC：PC 端鼠标 pointer:fine，不豁免
    if (el.hasAttribute && el.hasAttribute('data-action-atc') && el.closest('.t4s-sticky-atc__btns')) {
      var isTouchDevice = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      return isTouchDevice ? MP_SKIP_MA_KEY : null;
    }
    return null;
  }

  function _isMemberCbChecked() {
    var cb = document.querySelector('[data-mp-checkbox="member"]');
    return !!cb && cb.checked;
  }

  /**
   * 判断按钮 kind：checkout（直达结账）或 atc（加购 drawer）
   */
  function _getKind(el) {
    if (!el) return 'atc';
    var tag = el.tagName ? el.tagName.toLowerCase() : '';
    if (tag === 'quickly-buy-now-button') return 'checkout';
    if (el.hasAttribute('data-pdp-buy-now') || el.hasAttribute('data-buy-now')) return 'checkout';
    // SA 按钮组统一视为 checkout
    if (tag === 'shopify-accelerated-checkout' || tag === 'shopify-accelerated-checkout-cart') return 'checkout';
    if (el.classList && (el.classList.contains('shopify-payment-button'))) return 'checkout';
    return 'atc';
  }

  /**
   * 获取按钮的固定选择器（用于页面刷新后重新定位）。
   * 带父容器前缀确保唯一性，避免页面上同名 attr 的其他元素被误命中。
   */
  function _getBtnSelector(el) {
    if (!el) return null;
    var tag = el.tagName ? el.tagName.toLowerCase() : '';

    // 新 PDP 表单内
    if (el.hasAttribute('data-pdp-atc'))    return 'pdp-product-form [data-pdp-atc]';
    if (el.hasAttribute('data-pdp-buy-now')) return 'pdp-product-form [data-pdp-buy-now]';

    // TSM 加购
    if (el.hasAttribute('data-tsm-btn'))    return 'pdp-together-save-more [data-tsm-btn]';

    // sticky buy bar
    if (el.hasAttribute('data-buy-now'))    return '.sticky-buy-bar__inner [data-buy-now]';
    if (el.hasAttribute('data-action-atc') && el.closest('.sticky-buy-bar__inner'))
      return '.sticky-buy-bar__inner [data-action-atc]';

    // 旧 PDP 表单内
    if (el.hasAttribute('data-atc-form'))   return '.t4s-product-form__buttons [data-atc-form]';
    if (tag === 'quickly-buy-now-button')   return '.t4s-product-form__buttons quickly-buy-now-button';

    // 旧 PDP sticky ATC
    if (el.hasAttribute('data-action-atc') && el.closest('.t4s-sticky-atc__btns'))
      return '.t4s-sticky-atc__btns [data-action-atc]';

    // SA 按钮组（全局唯一）
    if (tag === 'shopify-accelerated-checkout')      return 'shopify-accelerated-checkout';
    if (tag === 'shopify-accelerated-checkout-cart') return 'shopify-accelerated-checkout-cart';
    if (el.classList && el.classList.contains('shopify-payment-button')) return '.shopify-payment-button';

    return null;
  }

  function _openLoginGate(gate) {
    _mpAtcGate = gate;
    // 兜底：第三方登录会直接 location.href 跳走，弹窗不会走正常关闭流程。
    // 在页面即将卸载时，若 gate 仍未保存，立即写入 sessionStorage，确保跳回后可回放。
    window.addEventListener('beforeunload', function () {
      if (_mpAtcGate) {
        _saveGateToSession(_mpAtcGate);
      }
    }, { once: true });
    var loginDialog = document.querySelector('login-dialog');
    if (loginDialog && typeof loginDialog.open === 'function') {
      loginDialog.open(null, {
        activityType: 'member_price',
        customTitle: i18n('unlockTitle', 'Unlock Your Member Price'),
        customDescription: i18n('unlockDescription', 'Sign in to unlock member-only savings & exclusive offers.'),
        footerButton: {
          text: i18n('continueWithoutMembership', 'Continue without membership'),
          onClick: function () { loginDialog.close(); },
        },
      });
      _watchLoginGateClose();
    } else {
      _replayAtcGate(gate);
    }
  }

  function _replayAtcGate(gate) {
    var g = gate || _mpAtcGate;
    _mpAtcGate = null;
    if (!g || !g.btn) {
      return;
    }
    // SA 按钮组 / 移动端 sticky ATC：DOM click 无法穿透第三方限制，
    // 关闭弹窗时写入豁免，72h 内不再拦截，避免反复弹窗影响用户体验。
    if (g.skipKey) {
      _setSkip(g.skipKey);
      return;
    }
    // 按钮可能处于上一次加购的 loading 状态（aria-disabled="true" + is-loading），
    // 此时 pdp.js 的 submit handler 会直接 return，导致回放无效。
    // 等待按钮恢复可用后再执行 click，超时 3s 则放弃。
    _waitBtnReady(g.btn, function () {
      window.__MP_REPLAY__ = true;
      try {
        g.btn.click();
      } finally {
        window.__MP_REPLAY__ = false;
      }
    });
  }

  /**
   * 等待按钮脱离 loading / disabled 状态后执行回调。
   * 若按钮当前已就绪则同步执行；否则用 MutationObserver 监听属性变化，
   * 超过 maxWait ms 后放弃（避免永久挂起）。
   */
  function _waitBtnReady(btn, cb, maxWait) {
    maxWait = maxWait || 3000;
    function isReady() {
      return !btn.hasAttribute('disabled') &&
             btn.getAttribute('aria-disabled') !== 'true' &&
             !btn.classList.contains('is-loading');
    }
    if (isReady()) { cb(); return; }
    var timer = setTimeout(function () {
      mo.disconnect();
    }, maxWait);
    var mo = new MutationObserver(function () {
      if (!isReady()) return;
      clearTimeout(timer);
      mo.disconnect();
      cb();
    });
    mo.observe(btn, { attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
  }

  /**
   * 登录后页面跳转前，把 gate 信息存入 sessionStorage。
   * 页面加载后由 _restoreGateAfterLogin() 读取并重触发。
   */
  function _saveGateToSession(gate) {
    if (!gate || !gate.selector) {
      return;
    }
    // 存储时记录原始页面 pathname，恢复时只在匹配的页面执行，避免承接页误删 sessionStorage
    var data = {
      kind: gate.kind,
      selector: gate.selector,
      pathname: window.location.pathname  // 存储 PDP 页路径
    };
    try {
      sessionStorage.setItem(MP_GATE_KEY, JSON.stringify(data));
    } catch (e) {
    }
  }

  /**
   * 页面加载时检查 sessionStorage，若有 gate 信息则恢复并自动触发原按钮。
   * 只在已登录状态下执行（防止非登录跳回时误触发）。
   * atc / checkout 统一找到按钮重新 click（buy-now 需 pdp.js buyNow() 重新创建 cart 跳 checkout）。
   */
  function _restoreGateAfterLogin() {
    var raw;
    try { raw = sessionStorage.getItem(MP_GATE_KEY); } catch (e) {}
    if (!raw) {
      return;
    }

    var saved;
    try { saved = JSON.parse(raw); } catch (e) {
      try { sessionStorage.removeItem(MP_GATE_KEY); } catch (e2) {}
      return;
    }
    if (!saved || !saved.selector) {
      try { sessionStorage.removeItem(MP_GATE_KEY); } catch (e) {}
      return;
    }

    // pathname 不匹配说明当前是承接页，不消费 sessionStorage，等 PDP 页加载后再执行
    if (saved.pathname && saved.pathname !== window.location.pathname) {
      return;
    }

    // 只有在匹配的页面才删除 sessionStorage
    try { sessionStorage.removeItem(MP_GATE_KEY); } catch (e) {}

    if (!isLoggedIn()) {
      return;
    }


    function doReplay() {
      var btn = document.querySelector(saved.selector);
      if (!btn) return;
      window.__MP_REPLAY__ = true;
      try { btn.click(); } finally { window.__MP_REPLAY__ = false; }
    }

    if (document.readyState === 'complete') {
      doReplay();
    } else {
      window.addEventListener('load', doReplay, { once: true });
    }
  }
  
  var _loginSuccessFlag = false;

  (function () {
    var bodyObs = new MutationObserver(function () {
      if (!document.body.classList.contains('momcozy-logined')) return;
      if (!_mpAtcGate) return;
      _loginSuccessFlag = true;
      _saveGateToSession(_mpAtcGate);
      _mpAtcGate = null;
      bodyObs.disconnect();
    });
    bodyObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  })();

  function _watchLoginGateClose() {
    // loginDialog.open() 可能是同步插入节点（第一次），也可能是复用已有节点仅改显隐（第二次+）。
    // 注册 MO 前先检查 .mc-login 是否已在 DOM，已在则直接标记 sawOpen，
    // 避免第二次弹窗时节点无变化导致 MO 永远感知不到"已打开"状态。
    var sawOpen = !!document.querySelector('.mc-login');
    var mo = new MutationObserver(function () {
      if (!_mpAtcGate) {
        mo.disconnect(); return;
      }
      var present = !!document.querySelector('.mc-login');
      if (present) { sawOpen = true; return; }
      if (sawOpen) {
        mo.disconnect();
        if (_hasMomcozyToken() || _loginSuccessFlag) {
          _loginSuccessFlag = false;
          if (_mpAtcGate) {
            _saveGateToSession(_mpAtcGate);
            _mpAtcGate = null;
          }
          return;
        }
        _replayAtcGate();
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  // 捕获阶段 click 拦截
  document.addEventListener('click', function (e) {
    if (window.__MP_REPLAY__ || _mpAtcGate) {
      return;
    }
    var t = e.target;
    if (!t || !t.closest) return;

    var btn = t.closest(SEL_ALL_INTERCEPT);
    var sa  = _findSaHost(e, t);
    if (!btn && !sa) return;
    if (isLoggedIn() || !_isMemberCbChecked()) {
      return;
    }

    // realBtn 需提前确定，豁免检查依赖它
    var realBtn = btn || sa;
    if (sa) {
      var path = e.composedPath ? e.composedPath() : [];
      for (var i = 0; i < path.length; i++) {
        if (path[i] && path[i].nodeType === 1) { realBtn = path[i]; break; }
      }
    }

    // 豁免检查：SA 按钮组 / 移动端 sticky ATC 在用户关闭弹窗后 72h 内不再拦截
    var skipKey = _getSkipKey(realBtn);
    if (skipKey && _isSkipActive(skipKey)) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    var kind     = _getKind(realBtn);
    var selector = _getBtnSelector(realBtn);
    _openLoginGate({ kind: kind, btn: realBtn, selector: selector, skipKey: skipKey });
  }, true);

  function _findSaHost(e, t) {
    // 命中范围：<shopify-accelerated-checkout> 宿主及其外层可点击容器。
    // 实测点击落在 div.shopify-payment-button（SDK 未激活时的 fallback 区域），
    // 它不在 <shopify-accelerated-checkout> 的 shadow 树内，仅匹配宿主会漏掉。
    var SA_SELECTOR = 'shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button, [data-shopify="payment-button"]';
    var host = t.closest(SA_SELECTOR);
    if (host) return host;
    if (!e.composedPath) return null;
    var p = e.composedPath();
    for (var i = 0; i < p.length; i++) {
      var el = p[i];
      if (!el || el.nodeType !== 1) continue;
      var tag = el.tagName;
      if (tag === 'SHOPIFY-ACCELERATED-CHECKOUT' || tag === 'SHOPIFY-ACCELERATED-CHECKOUT-CART') return el;
      if (el.classList && el.classList.contains('shopify-payment-button')) return el;
    }
    return null;
  }

  // submit 捕获兜底：Enter 键提交 / 程序化 submit 不经过按钮 click 时走此路径
  document.addEventListener('submit', function (e) {
    if (window.__MP_REPLAY__ || _mpAtcGate) return;
    var btn = e.submitter || (e.target.querySelector && e.target.querySelector('[data-pdp-atc], [data-atc-form]'));
    if (!btn || !(btn.hasAttribute('data-pdp-atc') || btn.hasAttribute('data-atc-form'))) return;
    if (isLoggedIn() || !_isMemberCbChecked()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var selector = _getBtnSelector(btn);
    _openLoginGate({ kind: 'atc', btn: btn, selector: selector });
  }, true);

  // 关闭弹窗 → 区分"用户手动关闭"和"登录成功后关闭"
  // LUTE:AUTH:CLOSED 在登录成功时也会同步派发（momcozy-login.es.js p(w): ct(token) → location.href → _(true) → dispatchEvent）
  // ct(token) 里已执行 body.classList.add('momcozy-logined')，所以此时检查 class 是可靠的
  /**
   * 判断是否刚完成登录（token 已写入 cookie 但页面尚未刷新）。
   * momcozy-login.es.js 的 p(w) 在 location.href 跳转前先执行 ct(token) 写 cookie，
   * 因此 LUTE:AUTH:CLOSED 派发时 cookie 已存在，可作为登录成功的同步判断依据。
   */
  function _hasMomcozyToken() {
    var cookieName = (window.__MC__CONFIG__ && window.__MC__CONFIG__.cookieName) || 'momcozy-token';
    return document.cookie.split(';').some(function (c) {
      return c.trim().startsWith(cookieName + '=');
    });
  }

  function _handleAuthClosed() {
    if (_hasMomcozyToken()) {
      if (_mpAtcGate) {
        _saveGateToSession(_mpAtcGate);
        _mpAtcGate = null;
      }
      return;
    }
    _replayAtcGate();
  }
  document.addEventListener('LUTE:AUTH:CLOSED', _handleAuthClosed);
  document.addEventListener('LUTE:AUTH:CLOSE',  _handleAuthClosed);

  // ── 事件：LUTE:AUTH:LOGIN ────────────────────────────────────────────────
  // 注意：登录成功时 LUTE:AUTH:CLOSED 先于此事件处理 gate（ct(token) 写 cookie 后同步派发 CLOSED），
  // _mpAtcGate 通常已被清掉。此处作为兜底。
  document.addEventListener('LUTE:AUTH:LOGIN', function (e) {
    if (e.detail && e.detail.type === 'keep') {
      return;
    }
    if (_mpAtcGate) {
      _saveGateToSession(_mpAtcGate);
      _mpAtcGate = null;
      return;
    }
    setTimeout(function () { window.location.reload(); }, 500);
  });
  //
  // ── 初始化入口 ────────────────────────────────────────────────────────────
  //
  // 核心问题：formatMoney 依赖 T4SThemeSP.Currency，在 theme-ready 之前不可用。
  // 新 PDP（pdp.js）：页面加载时派发 variant:changed（isInit=true），
  //   member-price.js 监听后调 initDiscountModule，但此时 Currency 可能未就绪。
  // 旧 PDP（theme.min.js）：不在加载时派发 variant:changed，
  //   必须靠 _init() 主动触发。
  //
  // 策略：
  //   1. theme-ready 时执行 _init()（Currency 已就绪，格式化正确）
  //   2. 若 theme-ready 已过（Currency 已就绪），立即执行
  //   3. variant:changed 里不再做格式化（只更新 _currentVariantId），
  //      延迟到 Currency 就绪后再渲染

  function _init() {
    const variantId = getCurrentVariantId();
    if (!variantId) return;
    const pd   = (window.__MP__ || {}).productData || {};
    const vMap = pd.variants || {};
    if (Object.keys(vMap).length > 0 && !vMap[String(variantId)]) return;
    _currentVariantId = variantId;
    initDiscountModule(variantId);
  }

  function _waitAndInit() {
    // 页面加载后先尝试恢复登录前保存的 gate（登录跳转回来时自动触发原按钮）
    _restoreGateAfterLogin();

    if (window.T4SThemeSP?.Currency?.formatMoney) {
      _init();
    } else {
      window.addEventListener('theme-ready', _init, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _waitAndInit, { once: true });
  } else {
    _waitAndInit();
  }

})();
